// =============== 📊 ENHANCED SCORING ENGINE ===============
// Additive scoring layer that wraps existing scoring without modification

import { calculateAdvancedScores } from '../../modules/extendedAssessment/advancedScoring';
import { getActiveCategories } from '../../modules/extendedAssessment/enhancedCategories';
import { calculateCareerFit } from './careerFit.js';
import { generateBehavioralNarrative } from './behavioralNarrative.js';
import { analyzePerformanceDrift } from './performanceDrift.js';

// Feature flags
const FEATURE_FLAGS = {
  ADVANCED_SCORING: import.meta.env?.VITE_ADVANCED_SCORING === 'true' || true
};

// =============== CONFIDENCE INTERVALS ===============
export const calculateConfidenceIntervals = (answers, category) => {
  const categoryAnswers = Object.entries(answers).filter(([id]) => {
    const categories = getActiveCategories();
    const cat = categories.find(c => c.id === category);
    return cat?.questions.some(q => q.id === id);
  });

  const sampleSize = categoryAnswers.length;
  
  // Calculate confidence based on sample size and response consistency
  let confidence = 0.5; // Base confidence
  
  if (sampleSize >= 20) confidence += 0.3;
  else if (sampleSize >= 10) confidence += 0.2;
  else if (sampleSize >= 5) confidence += 0.1;
  
  // Check response consistency (lower variance = higher confidence)
  const numericAnswers = categoryAnswers
    .map(([, answer]) => typeof answer === 'number' ? answer : null)
    .filter(a => a !== null);
    
  if (numericAnswers.length > 3) {
    const mean = numericAnswers.reduce((a, b) => a + b, 0) / numericAnswers.length;
    const variance = numericAnswers.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / numericAnswers.length;
    const consistency = Math.max(0, 1 - (variance / 2)); // Normalize variance
    confidence += consistency * 0.2;
  }
  
  confidence = Math.min(0.95, Math.max(0.3, confidence));
  
  return {
    overall: confidence,
    sampleSize,
    level: confidence >= 0.8 ? 'high' : confidence >= 0.6 ? 'medium' : 'low',
    margin: Math.round((1 - confidence) * 100) / 10 // Error margin in points
  };
};

// =============== ADAPTIVE METRICS ===============
export const calculateAdaptiveMetrics = (answers, category) => {
  const metrics = {
    responseConsistency: 0,
    engagementLevel: 0,
    cognitiveLoad: 0,
    adaptationRate: 0
  };

  const categoryAnswers = Object.entries(answers);
  if (categoryAnswers.length === 0) return metrics;

  // Response Consistency
  const likertAnswers = categoryAnswers
    .map(([, answer]) => typeof answer === 'number' && answer >= 1 && answer <= 5 ? answer : null)
    .filter(a => a !== null);
    
  if (likertAnswers.length > 2) {
    const mean = likertAnswers.reduce((a, b) => a + b, 0) / likertAnswers.length;
    const variance = likertAnswers.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / likertAnswers.length;
    metrics.responseConsistency = Math.max(0, 1 - (variance / 2));
  }

  // Engagement Level (based on text responses and non-neutral answers)
  const textResponses = categoryAnswers.filter(([, answer]) => 
    typeof answer === 'string' && answer.length > 10
  ).length;
  const nonNeutralAnswers = likertAnswers.filter(a => a !== 3).length;
  const totalAnswers = categoryAnswers.length;
  
  metrics.engagementLevel = (textResponses * 0.3 + nonNeutralAnswers * 0.1) / Math.max(1, totalAnswers);
  metrics.engagementLevel = Math.min(1, metrics.engagementLevel);

  // Cognitive Load (based on question difficulty and response time patterns)
  const categories = getActiveCategories();
  const cat = categories.find(c => c.id === category);
  let totalDifficulty = 0;
  let difficultyCount = 0;
  
  if (cat) {
    categoryAnswers.forEach(([questionId]) => {
      const question = cat.questions.find(q => q.id === questionId);
      if (question?.difficulty) {
        const difficultyScore = question.difficulty === 'hard' ? 1 : 
                               question.difficulty === 'medium' ? 0.6 : 0.3;
        totalDifficulty += difficultyScore;
        difficultyCount++;
      }
    });
  }
  
  metrics.cognitiveLoad = difficultyCount > 0 ? totalDifficulty / difficultyCount : 0.5;

  // Adaptation Rate (how well user adapts to increasing difficulty)
  metrics.adaptationRate = Math.min(1, metrics.responseConsistency * 0.7 + metrics.engagementLevel * 0.3);

  return metrics;
};

// =============== CROSS-DIMENSIONAL INSIGHTS ===============
export const calculateCrossDimensionalInsights = (allAnswers) => {
  const insights = [];
  const categories = getActiveCategories();
  
  // Calculate scores for each category
  const categoryScores = {};
  categories.forEach(cat => {
    const categoryAnswers = Object.entries(allAnswers).filter(([id]) => 
      cat.questions.some(q => q.id === id)
    );
    
    if (categoryAnswers.length > 0) {
      const baseScore = calculateAdvancedScores(cat.id, Object.fromEntries(categoryAnswers));
      categoryScores[cat.id] = baseScore.score || 50;
    }
  });

  // Cross-dimensional patterns
  const scores = categoryScores;
  
  // High EI + High Mindfulness = Excellent stress management
  if (scores.emotional_intelligence >= 75 && scores.mindfulness >= 75) {
    insights.push({
      type: 'synergy',
      title: 'Emotional Resilience Leader',
      description: 'Your high emotional intelligence combined with strong mindfulness creates exceptional stress management capabilities.',
      categories: ['emotional_intelligence', 'mindfulness'],
      strength: 'high'
    });
  }

  // High Cognitive + Low EI = Technical specialist
  if (scores.cognitive >= 80 && scores.emotional_intelligence < 60) {
    insights.push({
      type: 'development',
      title: 'Technical Excellence with EI Growth Opportunity',
      description: 'Strong analytical skills could be enhanced with emotional intelligence development for leadership roles.',
      categories: ['cognitive', 'emotional_intelligence'],
      strength: 'medium'
    });
  }

  // High Personality Openness + High Cognitive = Innovation potential
  if (scores.personality >= 70 && scores.cognitive >= 70) {
    insights.push({
      type: 'potential',
      title: 'Innovation Driver',
      description: 'Combination of openness and cognitive ability suggests strong innovation and problem-solving potential.',
      categories: ['personality', 'cognitive'],
      strength: 'high'
    });
  }

  // Balanced profile
  const scoreValues = Object.values(scores);
  const avgScore = scoreValues.reduce((a, b) => a + b, 0) / scoreValues.length;
  const variance = scoreValues.reduce((sum, val) => sum + Math.pow(val - avgScore, 2), 0) / scoreValues.length;
  
  if (variance < 100 && avgScore >= 65) {
    insights.push({
      type: 'balance',
      title: 'Well-Rounded Professional',
      description: 'Consistent performance across all dimensions indicates strong overall professional competency.',
      categories: Object.keys(scores),
      strength: 'high'
    });
  }

  // Low variance but low scores = consistent development needs
  if (variance < 100 && avgScore < 50) {
    insights.push({
      type: 'development',
      title: 'Systematic Development Opportunity',
      description: 'Consistent scores across dimensions suggest structured development approach would be most effective.',
      categories: Object.keys(scores),
      strength: 'medium'
    });
  }

  return insights;
};

// =============== PERSONALIZED DEVELOPMENT PATH ===============
export const generatePersonalizedPath = (baseScores, adaptiveMetrics, crossInsights) => {
  const developmentPath = {
    priority: 'medium',
    timeframe: '3-6 months',
    focus: [],
    recommendations: [],
    resources: []
  };

  const score = baseScores.score || 50;
  
  // Determine priority based on score and consistency
  if (score < 50 || adaptiveMetrics.responseConsistency < 0.4) {
    developmentPath.priority = 'high';
    developmentPath.timeframe = '1-3 months';
  } else if (score >= 75 && adaptiveMetrics.responseConsistency >= 0.7) {
    developmentPath.priority = 'low';
    developmentPath.timeframe = '6-12 months';
  }

  // Focus areas based on cross-dimensional insights
  const developmentInsights = crossInsights.filter(i => i.type === 'development');
  if (developmentInsights.length > 0) {
    developmentPath.focus = developmentInsights[0].categories;
  }

  // Generate recommendations based on score ranges
  if (score < 40) {
    developmentPath.recommendations.push('Focus on foundational skills development');
    developmentPath.recommendations.push('Seek mentorship or coaching support');
    developmentPath.resources.push('Basic skills training programs');
  } else if (score < 60) {
    developmentPath.recommendations.push('Target specific skill gaps identified in assessment');
    developmentPath.recommendations.push('Practice through structured exercises');
    developmentPath.resources.push('Skill-specific workshops and online courses');
  } else if (score < 80) {
    developmentPath.recommendations.push('Refine advanced competencies');
    developmentPath.recommendations.push('Take on stretch assignments');
    developmentPath.resources.push('Advanced training and certification programs');
  } else {
    developmentPath.recommendations.push('Focus on leadership and mentoring others');
    developmentPath.recommendations.push('Explore innovation and strategic thinking');
    developmentPath.resources.push('Executive development programs');
  }

  // Add engagement-specific recommendations
  if (adaptiveMetrics.engagementLevel < 0.5) {
    developmentPath.recommendations.push('Increase active participation in development activities');
    developmentPath.resources.push('Interactive learning platforms');
  }

  return developmentPath;
};

// =============== MAIN ENHANCED PROFILE FUNCTION ===============
export const calculateEnhancedProfile = (answers, category, timeSpent = {}, performanceData = {}) => {
  // Return original scoring if advanced features disabled
  if (!FEATURE_FLAGS.ADVANCED_SCORING) {
    return calculateAdvancedScores(category, answers);
  }

  // Get base scores (preserve original functionality)
  const baseScores = calculateAdvancedScores(category, answers);
  
  // Add enhanced analytics
  const confidenceIntervals = calculateConfidenceIntervals(answers, category);
  const adaptiveMetrics = calculateAdaptiveMetrics(answers, category);
  const crossInsights = calculateCrossDimensionalInsights(answers);
  const developmentPath = generatePersonalizedPath(baseScores, adaptiveMetrics, crossInsights);
  
  // Add holistic profile components
  const careerFit = calculateCareerFit(baseScores);
  const behavioralNarrative = generateBehavioralNarrative(answers, timeSpent, [category], performanceData);
  const performanceDrift = analyzePerformanceDrift(answers, timeSpent, Object.keys(answers));

  // Return enhanced profile
  return {
    ...baseScores, // Keep ALL original scoring data
    
    // Enhanced analytics
    enhanced: true,
    confidence: confidenceIntervals,
    adaptive: adaptiveMetrics,
    crossDimensional: crossInsights,
    development: developmentPath,
    
    // Holistic profile components
    careerFit,
    behavioralNarrative,
    performanceDrift,
    
    // Meta information
    enhancementVersion: '2.0',
    enhancedAt: new Date().toISOString()
  };
};

// =============== BATCH ENHANCEMENT ===============
export const enhanceAllCategoryScores = (allAnswers, allTimeSpent = {}, performanceData = {}) => {
  if (!FEATURE_FLAGS.ADVANCED_SCORING) {
    return null;
  }

  const categories = getActiveCategories();
  const enhancedScores = {};
  
  categories.forEach(cat => {
    const categoryAnswers = Object.entries(allAnswers).filter(([id]) => 
      cat.questions.some(q => q.id === id)
    );
    
    if (categoryAnswers.length > 0) {
      enhancedScores[cat.id] = calculateEnhancedProfile(
        Object.fromEntries(categoryAnswers), 
        cat.id,
        allTimeSpent,
        performanceData
      );
    }
  });

  // Generate holistic insights across all categories
  const holisticCareerFit = calculateCareerFit({
    bigFive: extractBigFiveFromCategories(enhancedScores),
    emotionalIntelligence: enhancedScores.emotional_intelligence?.score || 50,
    cognitiveAbility: enhancedScores.cognitive?.score || 50,
    mindfulness: enhancedScores.mindfulness?.score || 50
  });
  
  const holisticNarrative = generateBehavioralNarrative(allAnswers, allTimeSpent, categories, performanceData);
  const holisticDrift = analyzePerformanceDrift(allAnswers, allTimeSpent, Object.keys(allAnswers));

  return {
    categories: enhancedScores,
    crossDimensional: calculateCrossDimensionalInsights(allAnswers),
    overallConfidence: calculateOverallConfidence(enhancedScores),
    holistic: {
      careerFit: holisticCareerFit,
      behavioralNarrative: holisticNarrative,
      performanceDrift: holisticDrift
    },
    enhancedAt: new Date().toISOString()
  };
};

// Extract Big Five scores from category results
const extractBigFiveFromCategories = (categoryScores) => {
  const personality = categoryScores.personality;
  if (!personality || !personality.bigFive) {
    return {
      openness: 50,
      conscientiousness: 50,
      extraversion: 50,
      agreeableness: 50,
      neuroticism: 50
    };
  }
  return personality.bigFive;
};

// =============== OVERALL CONFIDENCE CALCULATION ===============
const calculateOverallConfidence = (categoryScores) => {
  const confidenceValues = Object.values(categoryScores)
    .map(score => score.confidence?.overall || 0.5)
    .filter(c => c > 0);
    
  if (confidenceValues.length === 0) return 0.5;
  
  const avgConfidence = confidenceValues.reduce((a, b) => a + b, 0) / confidenceValues.length;
  return Math.round(avgConfidence * 100) / 100;
};

// =============== EXPORTS ===============
export { FEATURE_FLAGS };
export default calculateEnhancedProfile;